import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAppContext } from '~/App';
import { SelectCombolist } from '~/components/SelectCombolist/SelectCombolist';
import { useUpdateAccountProfileMutation } from '~/generated/graphql';
import { getTenantTimezone } from '~/helpers/timezoneUtils';
import { PageProps } from './index';
import { getTimezoneOptions } from './timezoneUtils';

type DateAndTimeFormData = {
  timeZone: string;
};

type DateAndTimeSettingsProps = PageProps & {
  canManageAccount?: boolean;
};

export const DateAndTimeSettings = ({
  setShowPage,
  canManageAccount = false,
}: DateAndTimeSettingsProps) => {
  const { tenant, revalidate } = useAppContext();
  const { t } = useTranslation();
  const [, save] = useUpdateAccountProfileMutation();

  // Get tenant timezone from session - it's in snake_case: time_zone
  // The /api/sessions endpoint returns tenant.time_zone via TenantResource
  const tenantTimezone = tenant?.time_zone;

  // Get timezone options, including tenant timezone if it's not in the common list
  const timezoneOptions = useMemo(() => {
    const timezonesToInclude: string[] = [];
    if (tenantTimezone) {
      timezonesToInclude.push(tenantTimezone);
    }
    return getTimezoneOptions(...timezonesToInclude);
  }, [tenantTimezone]);

  // Use tenant timezone with safe fallback to browser timezone if not available
  const defaultTimezone = getTenantTimezone(tenant);

  const {
    handleSubmit,
    formState: { isSubmitting },
    watch,
    setValue,
    reset,
  } = useForm<DateAndTimeFormData>({
    defaultValues: {
      timeZone: defaultTimezone,
    },
  });

  const selectedTimezone = watch('timeZone');

  // Sync form value to tenant timezone when it's available
  useEffect(() => {
    if (tenantTimezone) {
      // Use reset to properly update the form value to tenant timezone
      reset({ timeZone: tenantTimezone }, { keepDefaultValues: false });
    }
  }, [tenantTimezone, reset]);

  const submit = async (values: DateAndTimeFormData) => {
    const result = await save({
      input: {
        name: tenant?.name ?? '',
        timeZone: values.timeZone,
      } as { name: string; timeZone?: string },
    });

    if (result.error?.message) {
      toast.error(result.error.message.replace('[GraphQL] ', ''));
      return;
    }

    if (result.data?.updateAccountProfile) {
      toast.success('Date and time settings updated successfully');
      setShowPage(null);
      revalidate();
    }
  };

  return (
    <div className='flex w-full grow'>
      <form
        onSubmit={handleSubmit(submit)}
        className='mx-auto flex flex-1 grow flex-col justify-between px-0 py-4'
      >
        <div className='flex flex-col gap-4'>
          {!canManageAccount && (
            <div className='mt-2 flex items-center gap-2 rounded-md bg-red-100 p-4 text-sm text-red-700'>
              <p>
                {t(
                  'You do not have permission to edit these settings. Contact your administrator if you think this is an error.'
                )}
              </p>
            </div>
          )}

          <div className='flex flex-col gap-1'>
            <label className='text-sm font-medium text-[#646872]'>
              Default time zone
            </label>
            <SelectCombolist
              label=''
              options={timezoneOptions}
              value={selectedTimezone}
              onChange={(value) => setValue('timeZone', value)}
              searchable={true}
            />
          </div>

          {/* Time format field - disabled as per design */}
          <div className='flex flex-col gap-1 opacity-20'>
            <label className='text-sm font-medium text-[#646872]'>
              Time format
            </label>
            <div className='flex h-[46px] items-center rounded-lg border border-[#bdc9c8] bg-white px-3 py-2'>
              <span className='text-base text-[#272930]'>
                DD MMM YYYY HH:MM AM/PM
              </span>
            </div>
          </div>
        </div>

        {canManageAccount && (
          <button
            type='submit'
            className='mt-6 w-full rounded-lg bg-[#288383] px-6 py-2 text-lg font-medium uppercase text-white transition-all duration-300 hover:bg-[#1f6b6b] disabled:opacity-50'
            disabled={isSubmitting || !canManageAccount}
          >
            {isSubmitting ? 'Saving...' : 'SAVE SETTINGS'}
          </button>
        )}
      </form>
    </div>
  );
};
